const REDIRECT_MODAL_CLASSES = 'checkout-modal visible PensioEmbeddedPaymentWindowBackground';
const REDIRECT_MODAL_SELECTOR = '.' + REDIRECT_MODAL_CLASSES.split(' ').join('.');
const REDIRECT_IFRAME_CLASS = 'redirect_iframe';
const REDIRECT_IFRAME_CLASSES = 'PensioEmbeddedPaymentWindow ' + REDIRECT_IFRAME_CLASS;
const REDIRECT_LOAD_TIMEOUT_MS = 45000;

let currentRedirect = null;
let redirectFrameSequence = 0;
let redirectAwaitingServerOutcome = false;
let awaitingServerOutcomeCloseListener = null;

function clearAwaitingServerOutcome() {
    redirectAwaitingServerOutcome = false;
    if (awaitingServerOutcomeCloseListener) {
        window.removeEventListener('closeEmbeddedPaymentWindow', awaitingServerOutcomeCloseListener);
        awaitingServerOutcomeCloseListener = null;
    }
}

function awaitServerOutcome(paymentId, frameName) {
    clearAwaitingServerOutcome();
    redirectAwaitingServerOutcome = true;
    awaitingServerOutcomeCloseListener = () => clearAwaitingServerOutcome();
    window.addEventListener('closeEmbeddedPaymentWindow', awaitingServerOutcomeCloseListener);
    window.dispatchEvent(new CustomEvent('altapayRedirectLoadFailed', {
        detail: {paymentId, frameName}
    }));
}

function isInitialAboutBlank(iframe) {
    try {
        const frameWindow = iframe.contentWindow;
        if (!frameWindow) {
            return false;
        }
        const href = frameWindow.location.href;
        return !href || href === 'about:blank';
    } catch (error) {
        return false;
    }
}

/**
 * @implements {IAltaPayMethodHandler}
 */
export class AltaPayGooglePay {

    paymentsClient;
    token;
    sessionId;
    paymentMethodId;
    order;
    paymentId;
    merchantId;
    merchantName;
    onInitiatePaymentUrl;
    operatingCountry;
    renderChecked;
    supportedNetworks;
    googlePayLoaded;
    environment;
    activeRedirect;

    constructor(
        token,
        sessionId,
        paymentMethodId,
        order,
        data) {
        this.token = token;
        this.sessionId = sessionId;
        this.paymentMethodId = paymentMethodId;
        this.order = order;
        this.merchantId = data.merchantId;
        this.merchantName = data.merchantName;
        this.operatingCountry = data.operatingCountry;
        this.supportedNetworks = data.supportedNetworks;
        this.onInitiatePaymentUrl = data.onInitiatePaymentUrl;
        this.environment = data.environment;
        this.renderChecked = false;
        this.activeRedirect = null;
        this.googlePayLoaded = this.googlePayLoad();
    }

    /**
     *
     * @returns {Promise}
     */
    googlePayLoad() {
        return new Promise(resolve => {
            if (window.google && window.google.payments && window.google.payments.api) {
                resolve();
                return;
            }
            const element = document.createElement("script");
            element.addEventListener("load", resolve, { once : true });
            element.src = "https://pay.google.com/gp/p/js/pay.js";
            document.head.appendChild(element);
        });
    }

    getGoogleIsReadyToPayRequest() {
        return Object.assign(
            {},
            {
                apiVersion: 2,
                apiVersionMinor: 0
            },
            {
                allowedPaymentMethods: [this.getBaseCardPaymentMethod()]
            }
        );
    }

    getBaseCardPaymentMethod() {
        return {
            type: 'CARD',
            parameters: {
                allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                allowedCardNetworks: this.supportedNetworks,
                allowPrepaidCards: false,
                allowCreditCards: true
            }
        };
    }

    getCardPaymentMethod() {
        return Object.assign(
            {},
            this.getBaseCardPaymentMethod(),
            {
                tokenizationSpecification: {
                    type: 'PAYMENT_GATEWAY',
                    parameters: {
                        'gateway': 'marketpay',
                        'gatewayMerchantId': this.merchantId
                    }
                }
            }
        );
    }

    getGooglePaymentDataRequest() {
        const paymentDataRequest = Object.assign({}, {
            apiVersion: 2,
            apiVersionMinor: 0
        });
        paymentDataRequest.allowedPaymentMethods = [this.getCardPaymentMethod()];
        paymentDataRequest.transactionInfo = this.getGoogleTransactionInfo();
        paymentDataRequest.merchantInfo = {
            merchantName: this.merchantName,
            merchantId: this.merchantId
        };

        return paymentDataRequest;
    }

    getGoogleTransactionInfo() {
        return {
            transactionId: this.sessionId,
            countryCode: this.operatingCountry,
            currencyCode: this.order.amount.currency,
            totalPriceStatus: 'FINAL',
            totalPrice: this.order.amount.value.toString()
        };
    }

    /**
     *
     * @returns {Promise}
     */
    onRenderCheck() {
        const error = (message) => Promise.reject({
            message,
            errorCode: 313009 // arbitrary error code
        });

        const that = this;
        return that.googlePayLoaded.then(() => {
            that.paymentsClient = new google.payments.api.PaymentsClient({environment: that.environment});
            const isReadyToPayRequest = that.getGoogleIsReadyToPayRequest();

            return that.paymentsClient.isReadyToPay(isReadyToPayRequest)
                .then(function(response) {
                    if (response.result) {
                        that.renderChecked = true;
                        return Promise.resolve();
                    } else {
                        return error("Google Pay is not available on the device.");
                    }
                })
                .catch(function(err) {
                    console.error("Error determining readiness to use Google Pay: ", err);
                    return error("Google Pay is not available at the moment.");
                });
        });
    }

    onInitiatePayment() {
        if (!this.renderChecked) {
            return Promise.reject({
                message: "Google Pay is not available on the device.",
                errorCode: 313009
            });
        }
        
        const that = this;
        let paymentRequest = {
            "sessionId": that.sessionId,
            "paymentMethodId": that.paymentMethodId
        };

        return fetch(that.onInitiatePaymentUrl, {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + that.token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentRequest)
        }).then(response => {
            if (response.ok) {
                return response.json();
            } else {
                throw new Error("Couldn't initiate Google Pay payment");
            }
        }).then(response => {
            that.paymentId = response.paymentId;
            return that.paymentsClient.loadPaymentData(that.getGooglePaymentDataRequest());
        }).then(paymentData => {
            return that.processPayment(paymentData);
        }).catch(err => {
            console.error("Error from Google Pay payment flow: ", err);
            if (err && err.statusCode === 'CANCELED') {
                // User closed the Google Pay window
                return Promise.resolve();
            } else {
                return Promise.reject({
                    message: (err && (err.statusMessage || err.message)) || "Google Pay error",
                    errorCode: 313009
                });
            }
        });
    }

    processPayment(paymentData) {
        const that = this;
        console.debug("Google Pay paymentData:", paymentData);
        
        let authorizeRequest = {
            "requestData": JSON.parse(paymentData.paymentMethodData.tokenizationData.token)
        };

        return fetch(that.onInitiatePaymentUrl + '/' + that.paymentId + '/authorization', {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + that.token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(authorizeRequest)
        }).then(response => {
            if (response.status !== 200) {
                throw new Error("Couldn't authorize Google Pay payment");
            }
            return response.json();
        }).then(data => {
            if (data.redirectResponse) {
                that.createIFrameWithRedirectContent(data.redirectResponse);
            } else if (data.resultUrl) {
                window.AltaPayUtils.redirect(data.resultUrl);
            } else if(data.resultContent) {
                that.createIFrameWithCallbackContent(data.resultContent);
            }
        });
    }

    #destroyActiveRedirect() {
        clearAwaitingServerOutcome();

        const active = currentRedirect;
        currentRedirect = null;
        this.activeRedirect = null;

        if (active) {
            if (active.loadTimeoutId) {
                clearTimeout(active.loadTimeoutId);
            }
            if (active.loadListener && active.iframe) {
                active.iframe.removeEventListener('load', active.loadListener);
            }
            if (active.owner) {
                active.owner.activeRedirect = null;
            }
            if (active.closeListener) {
                window.removeEventListener('closeEmbeddedPaymentWindow', active.closeListener);
            }
            if (active.modal && active.modal.parentNode) {
                console.debug('AltaPayGooglePay: tearing down redirect modal', {
                    paymentId: this.paymentId,
                    frameName: active.frameName
                });
                active.modal.parentNode.removeChild(active.modal);
            }
            document.body.style.overflow = active.previousOverflow;
        }

        const strays = this.#removeStrayRedirectModals();

        if (!active && strays > 0) {
            document.body.style.overflow = '';
        }
    }

    #removeStrayRedirectModals() {
        const strays = document.querySelectorAll(REDIRECT_MODAL_SELECTOR);
        strays.forEach((stray) => {
            console.warn('AltaPayGooglePay: removing stray redirect modal', {
                paymentId: this.paymentId
            });
            if (stray.parentNode) {
                stray.parentNode.removeChild(stray);
            }
        });
        return strays.length;
    }

    /**
     *
     * @param redirectResponse
     */
    createIFrameWithRedirectContent(redirectResponse) {
        this.#destroyActiveRedirect();

        const previousOverflow = document.body.style.overflow;
        try {
            document.body.style.overflow = 'hidden';
            const frameName = REDIRECT_IFRAME_CLASS + '_' + Date.now().toString(36) + '_' + (++redirectFrameSequence);
            const modal = document.createElement('div');
            modal.className = REDIRECT_MODAL_CLASSES;
            modal.style.position = 'fixed';
            modal.style.top = '0px';
            modal.style.left = '0px';
            modal.style.width = '100%';
            modal.style.height = '100%';
            modal.style.backgroundColor = 'rgba(200,200,200,0.35)';
            modal.style.display = 'flex';
            modal.style.justifyContent = 'center';
            modal.style.alignItems = 'center';
            modal.style.zIndex = '999999';

            const modalContainer = document.createElement('div');
            modalContainer.id = 'redirect_modal_container';
            modalContainer.style.display = 'flex';

            const iframe = document.createElement('iframe');
            iframe.name = frameName;
            iframe.id = REDIRECT_IFRAME_CLASS;
            iframe.className = REDIRECT_IFRAME_CLASSES;
            iframe.setAttribute('style', 'position:fixed;top:0px;left:0px;width:100%;height:100%;');
            iframe.setAttribute('allowTransparency', 'true');
            iframe.setAttribute('frameBorder', '0');

            modalContainer.appendChild(iframe);
            modal.appendChild(modalContainer);
            document.body.appendChild(modal);

            const form = document.createElement('form');
            form.method = redirectResponse.method || 'POST';
            form.action = redirectResponse.url;
            form.target = frameName;
            form.style.display = 'none';

            if (redirectResponse.data) {
                Object.entries(redirectResponse.data).forEach(([key, value]) => {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = value;
                    form.appendChild(input);
                });
            }

            document.body.appendChild(form);
            try {
                form.submit();
            } finally {
                if (form.parentNode === document.body) {
                    document.body.removeChild(form);
                }
            }

            const closeListener = () => this.#destroyActiveRedirect();

            const redirectRecord = {
                modal,
                iframe,
                frameName,
                closeListener,
                previousOverflow,
                owner: this,
                loadListener: null,
                loadTimeoutId: null
            };

            redirectRecord.loadListener = () => {
                if (isInitialAboutBlank(iframe)) {
                    return;
                }
                if (redirectRecord.loadTimeoutId) {
                    clearTimeout(redirectRecord.loadTimeoutId);
                    redirectRecord.loadTimeoutId = null;
                }
            };
            iframe.addEventListener('load', redirectRecord.loadListener);

            redirectRecord.loadTimeoutId = setTimeout(() => {
                console.warn('GooglePay: redirect iframe never loaded, tearing down modal and deferring the outcome to the gateway', {
                    paymentId: this.paymentId,
                    frameName
                });
                this.#destroyActiveRedirect();
                awaitServerOutcome(this.paymentId, frameName);
            }, REDIRECT_LOAD_TIMEOUT_MS);

            currentRedirect = redirectRecord;
            this.activeRedirect = redirectRecord;

            window.addEventListener('closeEmbeddedPaymentWindow', closeListener);
        } catch (error) {
            console.error('ERROR IN createIFrameWithRedirectHandling:', error);
            this.#destroyActiveRedirect();
            document.body.style.overflow = previousOverflow;
            awaitServerOutcome(this.paymentId, null);
        }
    }

    /**
     *
     * @param htmlContent
     */
    createIFrameWithCallbackContent(htmlContent){
        const background = document.createElement("div");
        background.className = 'AltaPayEmbeddedGooglePayResultsBackground';
        background.style.backgroundColor = 'white';
        background.style.position = 'fixed';
        background.style.top = '0px';
        background.style.left = '0px';
        background.style.width = '100%';
        background.style.height = '100%';

        const frame = document.createElement("iframe");
        frame.className = 'AltaPayEmbeddedGooglePayResults';
        frame.setAttribute('frameBorder', '0');
        frame.setAttribute('scrolling', 'auto');
        frame.setAttribute('style','position:fixed;top:0px;left:0px;width:100%;height:100%;');
        frame.srcdoc=htmlContent;

        document.body.appendChild(background);
        background.appendChild(frame);
    }

    /**
     * Checks whether a redirect modal (e.g. a 3DS challenge) is live anywhere on the page.
     * @returns {boolean}
     */
    isPendingActive() {
        const active = currentRedirect;
        if (active && active.modal && active.modal.isConnected) {
            return true;
        }
        return redirectAwaitingServerOutcome;
    }
}
